import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import PieChartGraph from 'src/Components/AdminComponents/Graphs/PieChartGraph';
import httpRequest from 'src/helpers/httpRequest';
import { CircularProgress } from '@mui/material';
import ProgressGraph from 'src/Components/AdminComponents/Graphs/ProgressGraph';
import SelectDropdown from 'src/Components/GlobalComponents/SelectDropdown';
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
    LineChart,
    Line
} from 'recharts';
import { allMonths, allYears, CURRENCY } from 'src/lib/constans';
import DiagnosticStates from 'src/Components/AdminComponents/DiagnosticStates';

interface IBioStats {
    name: string;
    biomarkerCount: number;
};
interface ICategories {
    id: string;
    name: string;
    totalCount: number;
};
interface IData {
    count: number;
    _id: { year: number; month: number };
    totalAmount: number;
};

interface IBioResponse {
    categoriesWithCounts: IBioStats[];
    totalBiomarkers: number;
};

const DiagnosticsOverview: React.FC = () => {
    const [biomarkerLoader, setBiomarkerLoader] = useState<boolean>(false);
    const [biomarkerData, setBiomarkerData] = useState<IBioResponse>({ categoriesWithCounts: [], totalBiomarkers: 0 });
    const fetchBiomarkersStats = async () => {
        setBiomarkerLoader(true);
        const { res, err } = await httpRequest<{ totalBiomarkers: number; categoriesWithCounts: IBioStats[] }[]>({ path: "/biomarker/graph/statistics" });
        if (res) {
            const { totalBiomarkers = 0, categoriesWithCounts = [] } = res[0];
            setBiomarkerData({ categoriesWithCounts, totalBiomarkers })
        } else {
            toast(err, { type: 'error' });
        }
        setBiomarkerLoader(false);
    };

    const [categoriesLoader, setCategoriesLoader] = useState<boolean>(false);
    const [categoriesData, setCategoriesData] = useState<ICategories[]>([]);
    const fetchTopSellingCategories = async () => {
        setCategoriesLoader(true);
        const { res, err } = await httpRequest<{ data: ICategories[] }>({ path: "/diagnostic-order/top/selling-categories" });
        if (res) {
            setCategoriesData(res?.data || []);
        } else {
            toast(err, { type: "error" });
        }
        setCategoriesLoader(false);
    };

    const [salesLoader, setSalesLoader] = useState<boolean>(false);
    const [saleData, setSaleData] = useState<{
        category: "monthly" | "yearly";
        totalCount: number;
        monthlyData: IData[];
        yearlyData: IData[];
        data: { name: string; count: number; }[];
    }>({
        category: "monthly",
        totalCount: 0,
        monthlyData: [],
        yearlyData: [],
        data: [],
    });
    const fetchSaleRecord = async () => {
        setSalesLoader(true);
        const { res, err } = await httpRequest<{
            totalCount: { count: number }[];
            monthlyData: IData[];
            yearlyData: IData[];
        }[]>({ path: "/diagnostic-order/graph/statistics/sales" });
        if (res) {
            setSaleData(prev => ({
                ...prev,
                totalCount: res[0]?.totalCount[0]?.count || 0,
                monthlyData: res[0]?.monthlyData,
                yearlyData: res[0]?.yearlyData,
            }));
        } else {
            toast(err, { type: "error" });
        }
        setSalesLoader(false);
    };

    const [revenueLoader, setRevenueLoader] = useState<boolean>(false);
    const [revenueData, setRevenueData] = useState<{
        category: "monthly" | "yearly";
        totalCount: number;
        monthlyData: IData[];
        yearlyData: IData[];
        data: { name: string; count: number; }[];
    }>({
        category: "monthly",
        totalCount: 0,
        monthlyData: [],
        yearlyData: [],
        data: [],
    });
    const fetchRevenueRecord = async () => {
        setRevenueLoader(true);
        const { res, err } = await httpRequest<{
            totalCount: { count: number }[];
            monthlyData: IData[];
            yearlyData: IData[];
        }[]>({ path: "/diagnostic-order/graph/statistics/revenue" });
        if (res) {
            setRevenueData(prev => ({
                ...prev,
                totalCount: res[0]?.totalCount[0]?.count || 0,
                monthlyData: res[0]?.monthlyData,
                yearlyData: res[0]?.yearlyData,
            }));
        } else {
            toast(err, { type: "error" });
        }
        setRevenueLoader(false);
    };

    useEffect(() => {
        fetchBiomarkersStats();
        fetchTopSellingCategories();
        fetchSaleRecord();
        fetchRevenueRecord();
    }, []);
    useEffect(() => {
        if (saleData?.category === 'monthly') {
            const formattedData = allMonths.map(({ month, name }) => {
                const v = saleData?.monthlyData.find(v => v._id.month === month);
                return {
                    name: name,
                    // count: v ? v.totalAmount : 0,
                    count: v ? v.count : 0,
                };
            });
            setSaleData(prev => ({ ...prev, data: formattedData }));
        } else if (saleData?.category === 'yearly') {
            const formattedData = allYears.map(year => {
                const v = saleData?.yearlyData.find(v => v._id.year === year);
                return {
                    name: String(year), // year name
                    // count: v ? v?.totalAmount : 0,
                    count: v ? v?.count : 0,
                };
            });
            setSaleData(prev => ({ ...prev, data: formattedData }));
        } else { }
    }, [saleData?.category, saleData?.totalCount]);

    useEffect(() => {
        if (revenueData?.category === 'monthly') {
            const formattedData = allMonths.map(({ month, name }) => {
                const v = revenueData?.monthlyData.find(v => v._id.month === month);
                return {
                    name: name,
                    count: v ? v.totalAmount : 0,
                };
            });
            setRevenueData(prev => ({ ...prev, data: formattedData }));
        } else if (revenueData?.category === 'yearly') {
            const formattedData = allYears.map(year => {
                const v = revenueData?.yearlyData.find(v => v._id.year === year);
                return {
                    name: String(year), // year name
                    count: v ? v?.totalAmount : 0,
                };
            });
            setRevenueData(prev => ({ ...prev, data: formattedData }));
        } else { }
    }, [revenueData?.category, revenueData?.totalCount]);
    return (
        <div className='gap-marging-admin'>
            <DiagnosticStates />
            <div className="parent-box parent-box-graph-height">
                <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
                    <div>
                        <h2 className="font-Poppins-SemiBold text-[#232323] font-size-20px mb-2">Sales</h2>
                        <div className='user-graph-inner'>
                            <div className='flex justify-between items-center'>
                                <div className='font-Poppins-Regular user-graph-header font-size-14px w-auto'>
                                    Total Sale: <span className='font-Poppins-SemiBold theme-color-green font-size-20px pl-1'>{saleData?.totalCount || 0}</span>
                                </div>
                                <div className='select-container-graph select-dropdown-container-graps w-auto'>
                                    <SelectDropdown
                                        options={[{ label: "Monthly", value: "monthly" }, { label: "Yearly", value: "yearly" }]}
                                        isMulti={false}
                                        data={[{ label: saleData?.category, value: saleData?.category }]}
                                        setData={(e) => setSaleData(prev => ({ ...prev, category: e[0]?.value }))}
                                        checkboxShow={false}
                                        isLabel={false}
                                        isRequired={true}
                                        clearIcon={false}
                                    />
                                </div>
                            </div>
                            {salesLoader
                                &&
                                <div className="text-center">
                                    <CircularProgress size={20} style={{ color: "#004540" }} />
                                </div>
                            }
                            <ResponsiveContainer className={'user-graph-container'}>
                                <BarChart
                                    data={saleData?.data}
                                    margin={{ top: 20, right: 0, left: -35, bottom: 0 }}
                                    barCategoryGap="15%"
                                >
                                    <CartesianGrid />
                                    <XAxis className='revenue-axis' dataKey="name" />
                                    <YAxis className='revenue-axis' />
                                    <Tooltip wrapperClassName='revenue-axis' />
                                    <Bar dataKey="count" fill="#00443F" radius={[10, 10, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <div>
                        <h2 className="font-Poppins-SemiBold text-[#232323] font-size-20px mb-2">Revenue</h2>
                        <div className='user-graph-inner'>
                            <div className='flex justify-between items-center'>
                                <div className='font-Poppins-Regular user-graph-header-revenue user-graph-header font-size-14px w-auto flex-nowrap'>
                                    Total revenue: <span className='font-Poppins-SemiBold theme-color-green font-size-20px'>{CURRENCY} {revenueData?.totalCount?.toFixed(2) || 0}</span>
                                </div>
                                <div className='select-container-graph select-dropdown-container-graps w-auto'>
                                    <SelectDropdown
                                        options={[{ label: "Monthly", value: "monthly" }, { label: "Yearly", value: "yearly" }]}
                                        isMulti={false}
                                        data={[{ label: revenueData?.category, value: revenueData?.category }]}
                                        setData={(e) => setRevenueData(prev => ({ ...prev, category: e[0]?.value }))}
                                        checkboxShow={false}
                                        isLabel={false}
                                        isRequired={true}
                                        clearIcon={false}
                                    />
                                </div>
                            </div>
                            {revenueLoader &&
                                <div className="text-center">
                                    <CircularProgress size={20} style={{ color: "#004540" }} />
                                </div>
                            }
                            <ResponsiveContainer className={'user-graph-container'}>
                                <LineChart data={revenueData?.data} margin={{ top: 20, right: 0, left: 0, bottom: 0 }}>
                                    <CartesianGrid />
                                    <XAxis className='revenue-axis' dataKey="name" />
                                    <YAxis className='revenue-axis' tickFormatter={(value) => `${value}`} />
                                    <Tooltip wrapperClassName='revenue-axis' />
                                    <Line
                                        type="monotone"
                                        dataKey="count"
                                        stroke="#008A80"
                                        dot={{ r: 5 }}
                                        activeDot={{ r: 7 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                    <ProgressGraph
                        data={categoriesData?.slice(0, 5)?.map(v => ({ label: v?.name, count: v?.totalCount }))}
                        totalCount={categoriesData?.slice(0, 5)?.reduce((prev, current) => { return prev + current?.totalCount }, 0)}
                        loader={categoriesLoader}
                        heading={"Top Selling Test Categories"}
                    />
                    <PieChartGraph
                        data={biomarkerData?.categoriesWithCounts?.map(v => ({ label: v?.name || "", count: v?.biomarkerCount || 0 }))}
                        totalCount={biomarkerData?.totalBiomarkers || 0}
                        loader={biomarkerLoader}
                        circleTitle={"Biomarkers"}
                        heading={"Biomarkers"}
                    />
                </div>
            </div>
        </div>
    );
};

export default DiagnosticsOverview;